import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    LOCALE_ID,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ApplicationService } from '@klickdata/core/application';
import { AuthService, GeneralCapability } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config/src/config.service';
import { Customer, CustomerLanding, CustomerService } from '@klickdata/core/customer';
import { GloalSearchService } from '@klickdata/core/global-search';
import { MessageService } from '@klickdata/core/message';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile/src/mobile.service';
import { QuickAccess } from '@klickdata/core/quick-access';
import { AppScope } from '@klickdata/core/resource';
import { User, UserActionHistory } from '@klickdata/core/user';
import { LoginFormComponent } from '@klickdata/guest/src/login/login-form/login-form.component';
import { UserAllowedResourcesService } from '@klickdata/home/src/shared/user-allowed-resources.service';
import { LocalStorageService } from 'ngx-webstorage';
import { Observable, of, Subject } from 'rxjs';
import { filter, first, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { QuickAccessUsersSelectComponent } from '../quick-access-users-select/quick-access-users-select.component';

enum Direction {
    Up = 'Up',
    Down = 'Down',
}
@Component({
    selector: 'app-header',
    templateUrl: 'header.component.html',
    styleUrls: ['header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [UserAllowedResourcesService],
})
export class HeaderComponent implements OnInit, OnChanges, OnDestroy {
    @Input() guest: boolean;
    @Input() onboarding: boolean;
    @Input() isHeaderWithOnlyLang: boolean;
    @ViewChild('scrollHorizontal') scrollHorizontal: ElementRef;
    public authenticated: Observable<boolean>;
    public developer = false;
    public toggleSearch = false;
    public active: string;
    public icon: string;
    public notifications: string[] = [];
    public destroy: Subject<boolean> = new Subject<boolean>();
    private platform: string;
    public version$: Observable<{ version: string; date: string }>;
    public user$: Observable<User>;
    public customer$: Observable<Customer>;
    private _dialogRef: MatDialogRef<LoginFormComponent>;
    public isSearchBoxActive: Observable<boolean>;
    public customerLanding$: Observable<CustomerLanding>;
    public countdown = 0;
    public favouriteQuickAccess: QuickAccess[] = [];
    public enabledQuickAccess: { user: QuickAccess[]; admin: QuickAccess[]; master: QuickAccess[] } = {
        user: [],
        admin: [],
        master: [],
    };
    public navbarQuickAccessData: QuickAccess[] = [];
    public logoTypeWidth: number = 80;
    public GeneralCapability = GeneralCapability;

    constructor(
        protected auth: AuthService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected config: ConfigService,
        protected application: ApplicationService,
        protected customerService: CustomerService,
        protected mobile: MobileService,
        protected dialog: MatDialog,
        protected storage: LocalStorageService,
        protected messageService: MessageService,
        public searchService: GloalSearchService,
        protected cdr: ChangeDetectorRef,
        protected bottomSheet: MatBottomSheet,
        protected userAllowedResourcesService: UserAllowedResourcesService,
        @Inject(LOCALE_ID) protected localeId: string
    ) {
        this.developer = config.config.buildMode === 'local';
        this.authenticated = this.auth.check();
        this.platform = this.auth.getNK3PlatformValue();
    }

    public ngOnInit() {
        this.isSearchBoxActive = this.searchService
            .getSearchBoxState()
            .pipe(tap((status) => (this.toggleSearch = status)));
        this.application
            .getNotifications()
            .pipe(takeUntil(this.destroy))
            .subscribe((notifications: string[]) => {
                this.notifications = notifications;
            });
        // this.customerLanding$ = this.auth.getCustomerLanding();
        this.customerLanding$ = this.customerService.getCustomerLogoType();
        this.active = this.router.url;
        this.version$ = this.application.getVersion();
        this.customer$ = this.auth.getCustomer();
        this.user$ = this.auth.getUser().pipe(
            shareReplay(1),
            tap((user) => {
                user.metadata.user_quick_links = user.metadata.user_quick_links.map((qa) => ({
                    ...qa,
                    platform: 'user',
                }));
                user.metadata.admin_quick_links = user.metadata.admin_quick_links.map((qa) => ({
                    ...qa,
                    platform: 'admin',
                }));
                user.metadata.master_quick_links = user.metadata.master_quick_links.map((qa) => ({
                    ...qa,
                    platform: 'master',
                }));

                this.enabledQuickAccess = {
                    user: user.metadata.user_quick_links.filter((qa) => qa.enabled),
                    admin: user.metadata.admin_quick_links.filter((qa) => qa.enabled && user.isAdmin()),
                    master: user.metadata.master_quick_links.filter((qa) => qa.enabled && user.isMasterAdmin()),
                };
                this.favouriteQuickAccess = [
                    ...user.metadata.user_quick_links.filter((qa) => qa.enabled),
                    ...user.metadata.admin_quick_links.filter((qa) => qa.enabled && user.isAdmin()),
                    ...user.metadata.master_quick_links.filter((qa) => qa.enabled && user.isMasterAdmin()),
                ].filter((link) => link.favorite);
                this.navbarQuickAccessData = [
                    ...user.metadata.user_quick_links.filter((qa) => qa.enabled),
                    ...user.metadata.admin_quick_links.filter((qa) => qa.enabled && user.isAdmin()),
                    ...user.metadata.master_quick_links.filter((qa) => qa.enabled && user.isMasterAdmin()),
                ].filter((link) => link.navbar);
            })
        );
        // reset header position with every route change
        this.router.events.pipe(takeUntil(this.destroy)).subscribe((val) => {
            if (val instanceof NavigationEnd) {
                document.getElementById('scoller-head')?.classList.remove('slide-down');
            }
            if (val instanceof NavigationStart) {
                if (this._dialogRef) {
                    this._dialogRef.close();
                }
            }
        });
    }
    public openHelp() {
        window.open(
            this.localeId === 'sv' ? 'https://klickdata.se/omoss/faq-klms' : 'https://klickdata.se/english/faq',
            '_blank'
        );
    }
    public addNavbarQuickAccess(user: User, customer: Customer) {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_NAVBAR_QUICK_ACCESS,
            data: {
                availableFavorites: this.favouriteQuickAccess.length < 3,
                availableNavbar: this.navbarQuickAccessData.length < 3,
                user: user,
                customer: customer,
                route: this.router.url,
                formArrayName: this.getformArrayNameByPlatform(this.platform),
            },
        });
        this.mobile
            .getSideNavResponseData()
            .pipe(
                filter((data) => !!data && !!data.route),
                switchMap((data: { route: string; customer_id: number }) =>
                    this.auth.getCustomer().pipe(
                        first(),
                        map((customer) => ({ route: data.route, customer } as { route: string; customer: Customer }))
                    )
                ),
                switchMap((data: { route: string; customer: Customer }) =>
                    this.bottomSheet
                        .open(QuickAccessUsersSelectComponent, {
                            data: { ...data, authUser: user },
                            panelClass: 'sheet-wrapper',
                        })
                        .afterDismissed()
                ),
                filter((data) => !!data),
                switchMap((data) => {
                    const { id, filters, ...rest } = data;
                    return this.customerService.update({
                        id: data.id,
                        quick_link: rest,
                    });
                }),
                takeUntil(this.destroy)
            )
            .subscribe();
    }
    public getformArrayNameByPlatform(platform: string) {
        return platform === 'user'
            ? 'user_quick_links'
            : platform === 'admin'
            ? 'admin_quick_links'
            : 'master_quick_links';
    }
    public ngOnChanges(changes: SimpleChanges): void {
        this.active = this.route.component['name'];
    }

    public onToggleMenu(): void {
        this.mobile.toggleMenu();
    }
    public onSearch() {
        this.router.navigate([`${this.platform === 'user' ? 'home' : this.platform}/search`], {});
    }
    addLink() {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_LINK_MATERIAL,
        });
    }
    public logout(): void {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'exit_to_app',
                title: $localize`Are you sure you want to quit ?`,
                contentBody: $localize`You are about to log out of KlickData KLMS.`,
                positiveBtn: $localize`Yes`,
                negativeBtn: $localize`No`,
            },
        });
        this.mobile
            .getSideNavAction()
            .pipe(filter((action) => action === SideNaveActionsTypes.POSITIVE))
            .subscribe((action) => {
                if (action) {
                    this.storage.clear(UserActionHistory.HOME_TAB_OVERVIEW);
                    this.auth.logout();
                }
            });
    }
    public openQuickAccessAUSettings(): void {
        this.router.navigate(['/home/user-profile-settings'], { queryParams: { tab: 'quick-access' } });
    }
    /**
     * Reset authentication and reload.
     * Used for debugging.
     */
    public reset(): void {
        // this.auth.reset(); // Reload only reset authentication by logout.
        window.location.reload();
    }
    checkResAllowedforAU(): Observable<boolean> {
        return this.platform == 'user' || this.platform == 'guest'
            ? this.userAllowedResourcesService.checkResAllowed(AppScope.MATERIAL)
            : of(true);
    }
    hasRecordingCapabilities(): Observable<boolean> {
        return this.customer$.pipe(map((customer) => customer.hasRecordingCapabilities));
    }
    public ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
        if (this._dialogRef) {
            this._dialogRef.close();
        }
    }
}
